import React, { use, useEffect, useState } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Collapse,
    Paper,
    Avatar,
    Typography,
    Pagination,
    Autocomplete,
    TextField
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import CheckboxAutocomplete from './CheckBoxAutoComplete';
import ExportExcel from './ExportExcel';

const RequirementsList = ({ requirements, requirementsPerPage }) => {
    const [openItems, setOpenItems] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [filteredRequirements, setFilteredRequirements] = useState(requirements)

    const totalPages = (Math.ceil(filteredRequirements.length / requirementsPerPage))
    const indexOfFirstRequirement = (currentPage - 1) * requirementsPerPage;
    const indexOfLastRequirement = Math.min(indexOfFirstRequirement + requirementsPerPage, filteredRequirements.length);
    const currentRequirements = filteredRequirements.slice(indexOfFirstRequirement, indexOfLastRequirement);


    // Update filtered requirements when requirements prop changes
    useEffect(() => {
        setFilteredRequirements(requirements);
    }, [requirements]);
    // Handle page change

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        // Scroll to the top of the requirements section
        const requirementsSection = document.getElementById('requirements-list-section');
        if (requirementsSection) {
            requirementsSection.scrollIntoView({ behavior: 'smooth' });
        }
    };
    const handleToggle = (id) => {
        setOpenItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleAutocompleteChange = (selectedOptions) => {
        if (selectedOptions.length === 0) {
            setFilteredRequirements(requirements);
        } else {
            const selectedIds = selectedOptions.map((opt) => opt.id);
            const filtered = requirements.filter((req) =>
                selectedIds.includes(req.query_id)
            );
            setFilteredRequirements(filtered);
        }
        // Reset to first page when filter changes
        setCurrentPage(1);
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflowY: 'auto', // Enable scrolling for the entire container
                maxHeight: 'calc(100vh - 100px)' // Adjust based on your layout
            }}>
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                overflow: "hidden" // Prevent overflow
            }}>
                <div style={{ width: '33%', display: 'flex', justifyContent: 'flex-start', padding: '5px' }}> {/* Flexible container aligned to end */}
                    <CheckboxAutocomplete
                        requirements={requirements}
                        onChange={handleAutocompleteChange}
                    />
                </div>

                <Typography variant="h6" sx={{
                    mb: 2,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    width: '33%',
                    px: 2 // Add some padding
                }}>
                    File Analysis Results ({requirements.length} requirements)
                </Typography>
                <div style={{ width: '33%', display: 'flex', justifyContent: 'flex-end' }}>
                    <ExportExcel requirements={requirements} />
                </div>

            </div>

            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                {currentRequirements.map((req, index) => (

                    <React.Fragment key={req.id || index}>
                        <ListItem
                            disablePadding
                            sx={{
                                bgcolor: '#f5f5f5',
                                mb: 1,
                                borderRadius: 1,
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            <ListItemButton onClick={() => handleToggle(req.id || index)}>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {`Requirement ${indexOfFirstRequirement + index + 1}: ${req.data.question.substring(0, 100)}${req.data.question.length > 100 ? '...' : ''}`}
                                        </Typography>
                                    }
                                />
                                {openItems[req.id || index] ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>
                        <Collapse in={openItems[req.id || index]} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: '#ffffff', mb: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                {/* User Message */}
                                <Paper
                                    className="message"
                                    style={{
                                        backgroundColor: "#d3d2d2",
                                        alignSelf: "flex-end",
                                        padding: "6px",
                                        marginBottom: "10px",
                                        maxWidth: "95%",
                                        wordWrap: "break-word",
                                        display: "flex",
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: '#152c73!important',
                                            width: "3rem",
                                            height: '3rem',
                                            marginRight: '10px',
                                            marginTop: '5px'
                                        }}
                                    >
                                        U
                                    </Avatar>
                                    <Box display={'flex'} width={"100%"} className="question-box">
                                        {req.data.question}
                                    </Box>
                                </Paper>

                                {/* AI Response */}
                                <Paper
                                    className="message"
                                    style={{
                                        backgroundColor: "#efefef",
                                        alignSelf: "flex-start",
                                        padding: "6px",
                                        maxWidth: "95%",
                                        wordWrap: "break-word",
                                        display: "flex",
                                        fontFamily: 'Roboto, sans-serif',
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            bgcolor: '#152c73!important',
                                            width: "3rem",
                                            height: '3rem',
                                            marginRight: '10px',
                                            marginTop: '5px'
                                        }}
                                    >
                                        AI
                                    </Avatar>
                                    <Box>
                                        {/* <h4>Modified Requirement:</h4> */}
                                        <ReactMarkdown>{req.data.aiResponse}</ReactMarkdown>
                                        {/* <div style={{ display: 'flex' }}>
                                            <h4>Original Score:</h4>
                                            <p style={{ alignSelf: 'center', marginLeft: '10px' }}>{req.data.aiResponse.score_obj.original_score}/100</p>
                                        </div>
                                        <div style={{ display: 'flex' }}>
                                            <h4>Revised Score:</h4>
                                            <p style={{ alignSelf: 'center', marginLeft: '10px' }}>{req.data.aiResponse.score_obj.revised_score}/100</p>
                                        </div>
                                        <h4>Reasoning:</h4>
                                        <ReactMarkdown>{req.data.aiResponse.score_obj.reasoning}</ReactMarkdown> */}
                                    </Box>
                                </Paper>
                            </Box>
                        </Collapse>
                    </React.Fragment>
                ))}
            </List>
            {/* Pagination controls */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        size="large"
                    />
                </Box>
            )}
        </Box>

    );
};

export default RequirementsList;
